import type { PdpScoreStatus, PdpStatus, PdpView } from '@vizit/api';
import type { ProductListingData } from '@vizit/molecules';
import { extractFilename } from '@vizit/utils';
import { isListingScoreWithheld } from '../../utils/formula-score-state';

const triggerCsvDownload = (blob: Blob, contentDisposition?: string | null) => {
  if (typeof document === 'undefined') {
    return;
  }

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  const fileName = extractFilename(contentDisposition ?? undefined) ?? 'products-export.csv';

  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// custom_retailer: drop once pnpm generate carries it.
type PdpListingView = PdpView & { custom_retailer?: string | null };

function asDate(date?: string | Date | null): Date | null {
  if (!date) return null;
  if (date instanceof Date) return date;
  return new Date(date);
}

/**
 * Maps an API `PdpView` onto the Library table's row model.
 *
 * Pre-launch (staged) pages were never live, so there is no live score to
 * diff against: their images are still scored at ingest, which would make
 * `score_change` look real, but the delta is meaningless — the Change column
 * renders a dash, matching the Live score column (whose dash the row derives
 * from `sourceType`).
 */
function toProductListingRow(p: PdpListingView): ProductListingData {
  const sourceType = p.source_type === 'manual' || p.source_type === 'scraped' ? p.source_type : null;
  const isPreLaunch = sourceType === 'manual';

  return {
    id: p.id,
    imageSrc: p.hero_image_url ?? null,
    imageThumbnailSrc: p.hero_image_url_360 ?? null,
    productName: p.name || 'Unnamed Product',
    asin: p.item_id,
    retailer: p.retailer || '-',
    benchmarkLabel: p.scores?.[0]?.benchmark_label ?? null,
    customRetailer: p.custom_retailer ?? null,
    score: p.listing_score_at_ingest ?? null,
    yourScore: p.listing_score ?? null,
    scoreChange:
      !isPreLaunch && p.listing_score_at_ingest != null && p.listing_score != null ? (p.score_change ?? null) : null,
    // The API types these as plain strings; the row model narrows them to the
    // known unions, and values outside them fall through the row's default
    // rendering branches just as null does.
    scoreStatus: (p.score_status as PdpScoreStatus | null | undefined) ?? null,
    isScoreWithheld: isListingScoreWithheld({
      scoreMethod: p.score_method,
      listingScore: p.listing_score,
      scoreStatus: p.score_status,
    }),
    sourceType,
    status: (p.status as PdpStatus | null | undefined) ?? null,
    lastRefreshed: asDate(p.last_refreshed),
    lastScraped: asDate(p.last_scraped),
    // `created_at` is required upstream; construct the Date directly so the
    // row's required `createdAt: Date` holds without an assertion.
    createdAt: new Date(p.created_at),
  };
}

export { triggerCsvDownload, asDate, toProductListingRow };
